<template>
  <div class="mosca-completa-counter">
    <div class="mc-section mc-total">
      <label class="mc-label">{{ t('moscaCompletaCounter.totalObserved') }}</label>
      <input
        type="number"
        min="0"
        :value="total"
        @change="updateTotal($event)"
        class="mc-input mc-input-large"
      />
    </div>

    <div class="mc-grid">
      <!-- Uova -->
      <div class="mc-field">
        <div class="mc-field-header">
          <label>{{ t('moscaCompletaCounter.eggs') }}</label>
          <span class="mc-pct">{{ pct('u') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="u"
          @change="updateField('u', $event)"
          class="mc-input"
        />
      </div>

      <!-- Larve verdi -->
      <div class="mc-field">
        <div class="mc-field-header">
          <label>L1v</label>
          <span class="mc-pct">{{ pct('l1v') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="l1v"
          @change="updateField('l1v', $event)"
          class="mc-input"
        />
      </div>

      <!-- Larve morte -->
      <div class="mc-field">
        <div class="mc-field-header">
          <label>L1m</label>
          <span class="mc-pct">{{ pct('l1m') }}%</span>
        </div>
        <input type="number" min="0" :value="l1m" @change="updateField('l1m', $event)" class="mc-input" />
      </div>

      <div class="mc-field">
        <div class="mc-field-header">
          <label>L2v</label>
          <span class="mc-pct">{{ pct('l2v') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="l2v"
          @change="updateField('l2v', $event)"
          class="mc-input"
        />
      </div>


      <div class="mc-field">
        <div class="mc-field-header">
          <label>L2m</label>
          <span class="mc-pct">{{ pct('l2m') }}%</span>
        </div>
        <input type="number" min="0" :value="l2m" @change="updateField('l2m', $event)" class="mc-input" />
      </div>

      <div class="mc-field">
        <div class="mc-field-header">
          <label>L3v</label>
          <span class="mc-pct">{{ pct('l3v') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="l3v"
          @change="updateField('l3v', $event)"
          class="mc-input"
        />
      </div>

      


      <div class="mc-field">
        <div class="mc-field-header">
          <label>L3m</label>
          <span class="mc-pct">{{ pct('l3m') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="l3m"
          @change="updateField('l3m', $event)"
          class="mc-input"
        />
      </div>

      <!-- Pupe -->
      <div class="mc-field">
        <div class="mc-field-header">
          <label>Pv</label>
          <span class="mc-pct">{{ pct('pv') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="pv"
          @change="updateField('pv', $event)"
          class="mc-input"
        />
      </div>

      <div class="mc-field">
        <div class="mc-field-header">
          <label>Pm</label>
          <span class="mc-pct">{{ pct('pm') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="pm"
          @change="updateField('pm', $event)"
          class="mc-input"
        />
      </div>

      <!-- Altro -->
      <div class="mc-field">
        <div class="mc-field-header">
          <label>FU</label>
          <span class="mc-pct">{{ pct('fu') }}%</span>
        </div>
        <input
          type="number"
          min="0"
          :value="fu"
          @change="updateField('fu', $event)"
          class="mc-input"
        />
      </div>
    </div>

    <!-- Warning se infestazione > totale -->
    <div v-if="hasWarning" class="mc-warning">
      <strong>{{ t('moscaCompletaCounter.warningTitle') }}</strong> {{ t('moscaCompletaCounter.warningExceedsTotal', { infested: totalInfested, total }) }}
    </div>

    <!-- Summary infestazione -->
    <div class="mc-summary" v-if="total > 0">
      <div class="mc-summary-item mc-item-active">
        <span class="mc-label">{{ t('moscaCompletaCounter.activeLabel') }}</span>
        <span class="mc-summary-value mc-active">{{ activeInfested }} ({{ pctActive }}%)</span>
      </div>
      <div class="mc-summary-item mc-item-harmful">
        <span class="mc-label">{{ t('moscaCompletaCounter.harmfulLabel') }}</span>
        <span class="mc-summary-value mc-harmful">{{ harmfulInfested }} ({{ pctHarmful }}%)</span>
      </div>
      <div class="mc-summary-item mc-item-total">
        <span class="mc-label">{{ t('moscaCompletaCounter.totalInfestations') }}</span>
        <span class="mc-summary-value mc-total">{{ totalInfested }} ({{ pctTotal }}%)</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      tot: 100,
      u: 0,
      l1v: 0,
      l2v: 0,
      l3v: 0,
      l1m: 0,
      l2m: 0,
      l3m: 0,
      pv: 0,
      pm: 0,
      fu: 0,
    })
  }
})
const emit = defineEmits(['update:modelValue'])

const total = computed(() => Number(props.modelValue?.tot) || 0)
const u = computed(() => Number(props.modelValue?.u) || 0)
const l1v = computed(() => Number(props.modelValue?.l1v) || 0)
const l2v = computed(() => Number(props.modelValue?.l2v) || 0)
const l3v = computed(() => Number(props.modelValue?.l3v) || 0)
const l1m = computed(() => Number(props.modelValue?.l1m) || 0)
const l2m = computed(() => Number(props.modelValue?.l2m) || 0)
const l3m = computed(() => Number(props.modelValue?.l3m) || 0)
const pv = computed(() => Number(props.modelValue?.pv) || 0)
const pm = computed(() => Number(props.modelValue?.pm) || 0)
const fu = computed(() => Number(props.modelValue?.fu) || 0)

function pct(field) {
  if (total.value === 0) return '—'
  const val = Number(props.modelValue?.[field]) || 0
  const percentage = Math.round((val / total.value) * 100)
  return percentage
}

const activeInfested = computed(() => {
  return u.value + l1v.value + l2v.value
})

const harmfulInfested = computed(() => {
  return l3v.value + l3m.value + pv.value + pm.value + fu.value
})

const totalInfested = computed(() => {
  return activeInfested.value + harmfulInfested.value + l1m.value + l2m.value
})

const pctActive = computed(() => {
  if (total.value === 0) return '—'
  return Math.round((activeInfested.value / total.value) * 100)
})

const pctHarmful = computed(() => {
  if (total.value === 0) return '—'
  return Math.round((harmfulInfested.value / total.value) * 100)
})

const pctTotal = computed(() => {
  if (total.value === 0) return '—'
  return Math.round((totalInfested.value / total.value) * 100)
})

const hasWarning = computed(() => {
  return total.value > 0 && totalInfested.value > total.value
})

function updateTotal(event) {
  const val = Math.max(0, parseInt(event.target.value) || 0)
  emit('update:modelValue', {
    ...props.modelValue,
    tot: val
  })
}

function updateField(field, event) {
  const val = Math.max(0, parseInt(event.target.value) || 0)
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: val
  })
}
</script>

<style scoped>
.mosca-completa-counter {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mc-section {
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.mc-total {
  background: #fffbeb;
  border-color: #fcd34d;
}

.mc-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.mc-input-large {
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
}

.mc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
}

.mc-field {
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.mc-field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.mc-field-header label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
}

.mc-pct {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2563eb;
  background: #dbeafe;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  white-space: nowrap;
}

.mc-input {
  width: 100%;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.5rem 0.25rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #1f2937;
}

.mc-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.mc-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.mc-summary-item {
  padding: 0.5rem;
  background: #fff;
  border-radius: 6px;
  border-left: 3px solid #ddd;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.mc-summary-item .mc-label {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: #666;
}

.mc-item-active {
  border-left-color: #fbbf24;
  background: #fffbeb;
}

.mc-item-harmful {
  border-left-color: #f87171;
  background: #fef2f2;
}

.mc-item-total {
  border-left-color: #16a34a;
  background: #f0fdf4;
}

.mc-summary-value {
  font-size: 1rem;
  font-weight: 700;
}

.mc-summary-value.mc-active {
  color: #d97706;
}

.mc-summary-value.mc-harmful {
  color: #dc2626;
}

.mc-summary-value.mc-total {
  color: #15803d;
}

.mc-warning {
  padding: 0.6rem 0.75rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #92400e;
  margin-bottom: 0.5rem;
}

.mc-warning strong {
  color: #d97706;
}

@media (max-width: 768px) {
  .mc-summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .mc-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
